import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme, { shadows } from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  ReferenceRanges,
  CustomTooltipTracker,
  calculateDomain,
  TrendChartSummarySection,
} from "../../../HealthChart/components";
import { useTrendChartCompactViewport } from "../../../HealthChart/hooks";
import {
  getSystolicColor,
  getDiastolicColor,
} from "../../../HealthChart/utils/bloodPressureChartColors";
import {
  getChartFocusStyles,
  getTrendChartLayoutVitals,
  trendGraphCardHorizontalPaddingSx,
  trendGraphCardPaperSx,
} from "../../../HealthChart/utils";
import { ChartDataPoint } from "../../../HealthChart/types";

// Type alias for blood pressure data points with required systolic/diastolic properties
export type BloodPressureDataPoint = ChartDataPoint &
  Required<Pick<ChartDataPoint, "systolic" | "diastolic">>;

export interface BloodPressureChartProps {
  data: BloodPressureDataPoint[];
}

type ViewMode = "total" | "systolic" | "diastolic";

export { getSystolicColor, getDiastolicColor };

// -------- Main Chart --------
const BloodPressureChart: React.FC<BloodPressureChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const isCompactChart = useTrendChartCompactViewport();
  const { right: chartRightMargin, yAxisWidth } = getTrendChartLayoutVitals(
    isCompactChart,
    27
  );
  const bloodPressureChartWidth = yAxisWidth;
  const [viewMode, setViewMode] = useState<ViewMode>("total");

  // Find the first selected point, or default to last data point (latest)
  const initialSelectedPoint =
    (data.find((point) => point.isSelected) || data[data.length - 1]) ?? null;

  const [selectedPoint, setSelectedPoint] =
    useState<BloodPressureDataPoint | null>(initialSelectedPoint);
  const [hoveredPoint, setHoveredPoint] =
    useState<BloodPressureDataPoint | null>(null);
  const hasAnimated = React.useRef(false);

  // Update selected point when data changes and a point has isSelected, or when data becomes non-empty
  React.useEffect(() => {
    if (data.length === 0) {
      return;
    }
    const newSelectedPoint = data.find((point) => point.isSelected);
    if (newSelectedPoint) {
      setSelectedPoint(newSelectedPoint);
    } else {
      setSelectedPoint((prev) =>
        prev === null ? data[data.length - 1] : prev
      );
    }
  }, [data]);

  if (data.length === 0) {
    return (
      <Box
        className="blood-pressure-chart"
        sx={{
          width: "100%",
          background: theme.palette.background.paper,
          borderRadius: 2.5,
          padding: 2,
          boxShadow: shadows.light,
        }}
      >
        <Typography variant="h4" sx={{ fontSize: 16, paddingBottom: 2 }}>
          {t("HealthChart.bloodPressureChart.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("HealthChart.bloodPressureChart.noData", "No chart data yet")}
        </Typography>
      </Box>
    );
  }

  const handlePointClick = (data: any) => {
    if (data && data.activePayload) {
      setSelectedPoint(data.activePayload[0].payload);
    }
  };

  const handleViewModeChange = (newMode: ViewMode) => {
    hasAnimated.current = true;
    setViewMode(newMode);
  };

  const activePoint = (hoveredPoint || selectedPoint) ?? data[data.length - 1];
  const activeIndex = data.findIndex((d) => d.date === activePoint.date);

  const allSystolicValues = data.map((d) => d.systolic);
  const allDiastolicValues = data.map((d) => d.diastolic);

  const systolicDomainData = calculateDomain(
    allSystolicValues,
    60,
    160,
    10
  ) as [number, number];
  const diastolicDomainData = calculateDomain(
    allDiastolicValues,
    30,
    120,
    10
  ) as [number, number];

  const systolicDomain: [number, number] = [
    60,
    Math.max(130, systolicDomainData[1]),
  ];
  const diastolicDomain: [number, number] = [
    30,
    Math.max(90, diastolicDomainData[1]),
  ];

  const totalDomain: [number, number] = [30, 180];
  const totalYTicks = [30, 60, 90, 120, 150];

  const yDomain =
    viewMode === "diastolic"
      ? diastolicDomain
      : viewMode === "systolic"
        ? systolicDomain
        : totalDomain;

  const systolicYTicks = [90, 120, 130];
  const diastolicYTicks = [50, 60, 80, 90];
  const yTicks =
    viewMode === "systolic"
      ? systolicYTicks
      : viewMode === "diastolic"
        ? diastolicYTicks
        : totalYTicks;

  const yTickFormatter = (value: number) =>
    value === yDomain[0] ? "" : value.toString();

  const systolicRanges = [
    {
      label: t("HealthChart.bloodPressureChart.zone.high"),
      yValue: 160,
      fontWeight: 700,
      color: themeColors.error,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.elevated"),
      yValue: 148,
      fontWeight: 700,
      color: themeColors.warning,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.normal"),
      yValue: 130,
      fontWeight: 700,
      color: themeColors.success,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.low"),
      yValue: 105,
      fontWeight: 700,
      color: themeColors.warning,
    },
  ];

  const diastolicRanges = [
    {
      label: t("HealthChart.bloodPressureChart.zone.high"),
      yValue: 118,
      fontWeight: 700,
      color: themeColors.error,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.elevated"),
      yValue: 105,
      fontWeight: 700,
      color: themeColors.warning,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.normal"),
      yValue: 92,
      fontWeight: 700,
      color: themeColors.success,
    },
    {
      label: t("HealthChart.bloodPressureChart.zone.low"),
      yValue: 75,
      fontWeight: 700,
      color: themeColors.warning,
    },
  ];

  const referenceRanges =
    viewMode === "systolic" ? systolicRanges : diastolicRanges;

  // Calculate Y positions for legend labels based on last data point
  const lastDataPoint = data[data.length - 1];
  const bloodPressureChartHeight = 200;
  const yMargin = { top: 10, bottom: 10 };
  const plotHeight = bloodPressureChartHeight - yMargin.top - yMargin.bottom;
  const yRange = yDomain[1] - yDomain[0];
  const systolicYPosition =
    yMargin.top +
    plotHeight * (1 - (lastDataPoint.systolic - yDomain[0]) / yRange);
  const diastolicYPosition =
    yMargin.top +
    plotHeight * (1 - (lastDataPoint.diastolic - yDomain[0]) / yRange);

  return (
    <>
      <style>{getChartFocusStyles("blood-pressure-chart")}</style>
      <Box className="blood-pressure-chart" sx={trendGraphCardPaperSx}>
        <Stack spacing={2} sx={trendGraphCardHorizontalPaddingSx}>
          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontSize: 16,
            }}
          >
            {t("HealthChart.bloodPressureChart.title")}
          </Typography>

          {/* Tab Buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {(["total", "systolic", "diastolic"] as ViewMode[]).map((mode) => (
              <Button
                key={mode}
                fullWidth
                onClick={() => handleViewModeChange(mode)}
                variant={viewMode === mode ? "contained" : "outlined"}
                sx={{
                  paddingTop: convertPxToRem(6),
                  paddingBottom: convertPxToRem(6),
                  paddingLeft: convertPxToRem(10),
                  paddingRight: convertPxToRem(10),
                  background:
                    viewMode === mode
                      ? themeColors.primary
                      : theme.palette.primary[100],
                  color:
                    viewMode === mode ? "white" : theme.palette.primary.main,
                  ...(viewMode !== mode && {
                    borderColor: theme.palette.primary.main,
                  }),
                }}
              >
                {t(`HealthChart.bloodPressureChart.view.${mode}`)}
              </Button>
            ))}
          </Box>

          {/* Chart Container */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 200,
              outline: "none",
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={data}
                margin={{
                  top: 10,
                  right: chartRightMargin,
                  left: 0,
                  bottom: 10,
                }}
                onClick={handlePointClick}
              >
                {/* Grid */}
                <CartesianGrid
                  stroke={theme.palette.grey[300]}
                  strokeDasharray="5 5"
                  vertical={false}
                  horizontal={viewMode !== "total"}
                />

                {/* Reference Lines and Zones for Systolic/Diastolic Views */}
                {viewMode === "systolic" && (
                  <>
                    {/* Background zones */}
                    <ReferenceArea
                      y1={130}
                      y2={yDomain[1]}
                      fill={backgroundColors.errorLight}
                      fillOpacity={0.5}
                    />
                    <ReferenceArea
                      y1={120}
                      y2={130}
                      fill={backgroundColors.warningLight}
                      fillOpacity={0.5}
                    />
                    <ReferenceArea
                      y1={90}
                      y2={120}
                      fill={backgroundColors.successLight}
                      fillOpacity={0.5}
                    />
                    <ReferenceArea
                      y1={yDomain[0]}
                      y2={90}
                      fill={backgroundColors.warningLight}
                      fillOpacity={0.5}
                    />

                    {/* Dashed lines */}
                    <ReferenceLine
                      y={130}
                      stroke={themeColors.error}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                    />
                    <ReferenceLine
                      y={120}
                      stroke={themeColors.warning}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                    />
                    <ReferenceLine
                      y={90}
                      stroke={themeColors.warning}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                    />
                  </>
                )}

                {viewMode === "diastolic" && (
                  <>
                    <ReferenceArea
                      y1={90}
                      y2={yDomain[1]}
                      fill={backgroundColors.errorLight}
                      fillOpacity={0.5}
                    />
                    <ReferenceArea
                      y1={81}
                      y2={90}
                      fill={backgroundColors.warningLight}
                      fillOpacity={0.5}
                    />
                    <ReferenceArea
                      y1={60}
                      y2={80}
                      fill={backgroundColors.successLight}
                      fillOpacity={0.5}
                    />
                    <ReferenceArea
                      y1={yDomain[0]}
                      y2={60}
                      fill={backgroundColors.warningLight}
                      fillOpacity={0.5}
                    />
                    <ReferenceLine
                      y={90}
                      stroke={themeColors.error}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                    />
                    <ReferenceLine
                      y={80}
                      stroke={themeColors.warning}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                    />
                    <ReferenceLine
                      y={60}
                      stroke={themeColors.warning}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                    />
                  </>
                )}

                {/* X Axis */}
                <XAxis
                  dataKey="date"
                  tick={{
                    fontSize: 10,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  dy={5}
                />

                <YAxis
                  domain={yDomain}
                  ticks={yTicks}
                  interval={0}
                  tickFormatter={yTickFormatter}
                  tick={{
                    fontSize: 11,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  width={bloodPressureChartWidth}
                />

                {/* Tooltip with custom cursor for vertical dashed line - placed before Lines so it renders behind dots */}
                <Tooltip
                  content={(props) => (
                    <CustomTooltipTracker
                      {...props}
                      setHoveredPoint={setHoveredPoint}
                    />
                  )}
                  cursor={{
                    stroke: theme.palette.grey[900],
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                />

                {/* Systolic Line */}
                {viewMode !== "diastolic" && (
                  <Line
                    type="linear"
                    dataKey="systolic"
                    stroke={theme.palette.grey[300]}
                    strokeWidth={2}
                    dot={(props: any) => (
                      <CustomDot
                        {...props}
                        viewMode={viewMode}
                        getColorFn={getSystolicColor}
                      />
                    )}
                    activeDot={(props: any) => (
                      <CustomActiveDot
                        {...props}
                        getColorFn={getSystolicColor}
                      />
                    )}
                    isAnimationActive={hasAnimated.current}
                    animationDuration={800}
                    animationEasing="ease-in-out"
                  />
                )}

                {/* Diastolic Line */}
                {viewMode !== "systolic" && (
                  <Line
                    type="linear"
                    dataKey="diastolic"
                    stroke={theme.palette.grey[300]}
                    strokeWidth={2}
                    dot={(props: any) => (
                      <CustomDot
                        {...props}
                        viewMode={viewMode}
                        getColorFn={getDiastolicColor}
                      />
                    )}
                    activeDot={(props: any) => (
                      <CustomActiveDot
                        {...props}
                        getColorFn={getDiastolicColor}
                      />
                    )}
                    isAnimationActive={hasAnimated.current}
                    animationDuration={800}
                    animationEasing="ease-in-out"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>

            {/* Reference Range Labels */}
            {viewMode !== "total" && (
              <ReferenceRanges
                yDomain={yDomain}
                chartHeight={bloodPressureChartHeight}
                yMargin={yMargin}
                ranges={referenceRanges}
              />
            )}

            {/* Legend Labels */}
            {viewMode === "total" && (
              <>
                {/* Systolic Label */}
                <div
                  style={{
                    position: "absolute",
                    right: 6,
                    top: systolicYPosition - 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: theme.palette.grey[700],
                    }}
                  >
                    {t("HealthChart.bloodPressureChart.label.systolic")}
                  </span>
                </div>
                {/* Diastolic Label */}
                <div
                  style={{
                    position: "absolute",
                    right: 2,
                    top: diastolicYPosition - 28,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: theme.palette.grey[700],
                    }}
                  >
                    {t("HealthChart.bloodPressureChart.label.diastolic")}
                  </span>
                </div>
              </>
            )}
          </Box>

          <TrendChartSummarySection
            activeIndex={activeIndex}
            dataLength={data.length}
            chevronLeftMargin={bloodPressureChartWidth}
            chevronRightMargin={chartRightMargin}
            chevronOnlyWithMultipleDataPoints={false}
            datePrefix={t("HealthChart.bloodPressureChart.datePrefix")}
            fullDate={activePoint.fullDate}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color:
                    viewMode === "diastolic"
                      ? theme.palette.grey[300]
                      : theme.palette.grey[700],
                }}
              >
                {activePoint.systolic}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color:
                    viewMode === "total"
                      ? theme.palette.grey[700]
                      : theme.palette.grey[300],
                }}
              >
                /
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color:
                    viewMode === "systolic"
                      ? theme.palette.grey[300]
                      : theme.palette.grey[700],
                }}
              >
                {activePoint.diastolic}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(14),
                  color: theme.palette.grey[900],
                  marginLeft: convertPxToRem(4),
                }}
              >
                {t("HealthChart.bloodPressureChart.unit")}
              </span>
            </div>
          </TrendChartSummarySection>
        </Stack>
      </Box>
    </>
  );
};

export default BloodPressureChart;
